/**
 * Pose maths for the animation system.
 *
 * A POSE is two flat typed arrays, never objects:
 *   q   Float32Array(BONE_COUNT * 4)   local rotation, xyzw
 *   p   Float32Array(BONE_COUNT * 3)   local position OFFSET from the bind pose
 *                                      (metres, rig-local). Only `root` and
 *                                      `hips` normally carry one.
 *
 * Nothing in this file allocates. Every routine takes its destination buffer.
 */
import { BONE_COUNT, BONE_PARENT } from '../chars/rig.js';

export const NB = BONE_COUNT;

/** Fresh rotation buffer, identity everywhere. */
export function makeQ() {
  const q = new Float32Array(NB * 4);
  for (let i = 0; i < NB; i++) q[i * 4 + 3] = 1;
  return q;
}
export function makeP() { return new Float32Array(NB * 3); }
/** 3x4 row-major skinning matrices, one per bone. */
export function makeM() {
  const m = new Float32Array(NB * 12);
  for (let i = 0; i < NB; i++) { m[i * 12] = 1; m[i * 12 + 5] = 1; m[i * 12 + 10] = 1; }
  return m;
}

/** Euler XYZ (radians, 3/bone) -> quaternion (4/bone). Matches THREE's order. */
export function eulerToQuat(e, q) {
  for (let i = 0; i < NB; i++) {
    const x = e[i * 3] * 0.5, y = e[i * 3 + 1] * 0.5, z = e[i * 3 + 2] * 0.5;
    const c1 = Math.cos(x), s1 = Math.sin(x);
    const c2 = Math.cos(y), s2 = Math.sin(y);
    const c3 = Math.cos(z), s3 = Math.sin(z);
    const o = i * 4;
    q[o] = s1 * c2 * c3 + c1 * s2 * s3;
    q[o + 1] = c1 * s2 * c3 - s1 * c2 * s3;
    q[o + 2] = c1 * c2 * s3 + s1 * s2 * c3;
    q[o + 3] = c1 * c2 * c3 - s1 * s2 * s3;
  }
  return q;
}

/** out = slerp(a, b, t) for every bone. Safe when t is 0 or 1. */
export function blendQ(a, b, t, out) {
  if (t <= 0) { if (out !== a) out.set(a); return out; }
  if (t >= 1) { if (out !== b) out.set(b); return out; }
  for (let i = 0; i < NB; i++) {
    const o = i * 4;
    let ax = a[o], ay = a[o + 1], az = a[o + 2], aw = a[o + 3];
    const bx = b[o], by = b[o + 1], bz = b[o + 2], bw = b[o + 3];
    let cos = ax * bx + ay * by + az * bz + aw * bw;
    let s = 1;
    if (cos < 0) { cos = -cos; s = -1; }
    let k0, k1;
    if (cos > 0.9995) { k0 = 1 - t; k1 = t * s; }
    else {
      const th = Math.acos(cos), st = Math.sin(th);
      k0 = Math.sin((1 - t) * th) / st;
      k1 = (Math.sin(t * th) / st) * s;
    }
    out[o] = ax * k0 + bx * k1; out[o + 1] = ay * k0 + by * k1;
    out[o + 2] = az * k0 + bz * k1; out[o + 3] = aw * k0 + bw * k1;
  }
  return out;
}

/** out = lerp(a, b, t) for position buffers. */
export function blendP(a, b, t, out) {
  const n = NB * 3;
  for (let i = 0; i < n; i++) out[i] = a[i] + (b[i] - a[i]) * t;
  return out;
}

/**
 * Additive layer: out = base * delta^w, applied per bone (local space).
 * `delta` is an euler-derived quaternion representing an OFFSET, not a pose.
 */
export function addQ(base, delta, w, out, mask) {
  for (let i = 0; i < NB; i++) {
    const o = i * 4;
    const k = mask ? mask[i] * w : w;
    if (k === 0) { if (out !== base) { out[o] = base[o]; out[o + 1] = base[o + 1]; out[o + 2] = base[o + 2]; out[o + 3] = base[o + 3]; } continue; }
    // scale the delta by k (nlerp from identity — deltas are small)
    let dx = delta[o], dy = delta[o + 1], dz = delta[o + 2], dw = delta[o + 3];
    if (dw < 0) { dx = -dx; dy = -dy; dz = -dz; dw = -dw; }
    dx *= k; dy *= k; dz *= k; dw = 1 + (dw - 1) * k;
    const il = 1 / (Math.hypot(dx, dy, dz, dw) || 1);
    dx *= il; dy *= il; dz *= il; dw *= il;
    const bx = base[o], by = base[o + 1], bz = base[o + 2], bw = base[o + 3];
    out[o] = bw * dx + bx * dw + by * dz - bz * dy;
    out[o + 1] = bw * dy + by * dw + bz * dx - bx * dz;
    out[o + 2] = bw * dz + bz * dw + bx * dy - by * dx;
    out[o + 3] = bw * dw - bx * dx - by * dy - bz * dz;
  }
  return out;
}

/**
 * Local rotations + position offsets -> world bone matrices AND skinning
 * matrices, both 3x4 row-major.
 *
 * @param q     Float32Array(NB*4) local rotations
 * @param p     Float32Array(NB*3) local position offsets (added to rig.pos)
 * @param rig   from makeRig()
 * @param world Float32Array(NB*12) out: bone world matrices
 * @param skin  Float32Array(NB*12) out: world * inverseBind  (may be null)
 */
export function poseToMatrices(q, p, rig, world, skin) {
  const rp = rig.pos, bw = rig.world;
  for (let i = 0; i < NB; i++) {
    const o4 = i * 4, o3 = i * 3, om = i * 12;
    const x = q[o4], y = q[o4 + 1], z = q[o4 + 2], w = q[o4 + 3];
    const x2 = x + x, y2 = y + y, z2 = z + z;
    const xx = x * x2, xy = x * y2, xz = x * z2;
    const yy = y * y2, yz = y * z2, zz = z * z2;
    const wx = w * x2, wy = w * y2, wz = w * z2;
    // local 3x3
    const l00 = 1 - (yy + zz), l01 = xy - wz, l02 = xz + wy;
    const l10 = xy + wz, l11 = 1 - (xx + zz), l12 = yz - wx;
    const l20 = xz - wy, l21 = yz + wx, l22 = 1 - (xx + yy);
    const tx = rp[o3] + p[o3], ty = rp[o3 + 1] + p[o3 + 1], tz = rp[o3 + 2] + p[o3 + 2];
    const par = BONE_PARENT[i];
    if (par < 0) {
      world[om] = l00; world[om + 1] = l01; world[om + 2] = l02; world[om + 3] = tx;
      world[om + 4] = l10; world[om + 5] = l11; world[om + 6] = l12; world[om + 7] = ty;
      world[om + 8] = l20; world[om + 9] = l21; world[om + 10] = l22; world[om + 11] = tz;
    } else {
      const pm = par * 12;
      const a00 = world[pm], a01 = world[pm + 1], a02 = world[pm + 2], a03 = world[pm + 3];
      const a10 = world[pm + 4], a11 = world[pm + 5], a12 = world[pm + 6], a13 = world[pm + 7];
      const a20 = world[pm + 8], a21 = world[pm + 9], a22 = world[pm + 10], a23 = world[pm + 11];
      world[om] = a00 * l00 + a01 * l10 + a02 * l20;
      world[om + 1] = a00 * l01 + a01 * l11 + a02 * l21;
      world[om + 2] = a00 * l02 + a01 * l12 + a02 * l22;
      world[om + 3] = a00 * tx + a01 * ty + a02 * tz + a03;
      world[om + 4] = a10 * l00 + a11 * l10 + a12 * l20;
      world[om + 5] = a10 * l01 + a11 * l11 + a12 * l21;
      world[om + 6] = a10 * l02 + a11 * l12 + a12 * l22;
      world[om + 7] = a10 * tx + a11 * ty + a12 * tz + a13;
      world[om + 8] = a20 * l00 + a21 * l10 + a22 * l20;
      world[om + 9] = a20 * l01 + a21 * l11 + a22 * l21;
      world[om + 10] = a20 * l02 + a21 * l12 + a22 * l22;
      world[om + 11] = a20 * tx + a21 * ty + a22 * tz + a23;
    }
    if (skin) {
      // bind is a pure translation (all bind rotations are identity), so
      // skin = world * T(-bindWorld) keeps the 3x3 and shifts the column.
      const bx = bw[o3], by = bw[o3 + 1], bz = bw[o3 + 2];
      skin[om] = world[om]; skin[om + 1] = world[om + 1]; skin[om + 2] = world[om + 2];
      skin[om + 3] = world[om + 3] - (world[om] * bx + world[om + 1] * by + world[om + 2] * bz);
      skin[om + 4] = world[om + 4]; skin[om + 5] = world[om + 5]; skin[om + 6] = world[om + 6];
      skin[om + 7] = world[om + 7] - (world[om + 4] * bx + world[om + 5] * by + world[om + 6] * bz);
      skin[om + 8] = world[om + 8]; skin[om + 9] = world[om + 9]; skin[om + 10] = world[om + 10];
      skin[om + 11] = world[om + 11] - (world[om + 8] * bx + world[om + 9] * by + world[om + 10] * bz);
    }
  }
  return world;
}
